import type { MyProjects } from "../api/schemas";

/** The values of an activity the selection needs (T-184). */
export interface ActivityOption {
  /** Identifier of the activity. */
  readonly id: string;
  /** Short code, unique in the tenant. */
  readonly code: string;
  /** Name shown with the code. */
  readonly name: string;
}

/**
 * Returns the activities a person can choose for a project (spec 6.7): the active activities, limited
 * to the list of the project when it has one. Caches from before T-184 have none.
 *
 * @param myProjects - Projects, activities and allowed lists of the person (`GET /me/projects`).
 * @param projectId - The project, or `null` for none.
 * @returns The activities ordered by code; empty without a project.
 */
export function activitiesForProject(
  myProjects: MyProjects | null,
  projectId: string | null,
): ActivityOption[] {
  if (myProjects === null || projectId === null) {
    return [];
  }

  const activities = myProjects.activities ?? [];
  const allowed = (myProjects.allowedActivities ?? []).find((item) => item.projectId === projectId);
  const choosable = activities
    .filter((activity) => allowed === undefined || allowed.activityIds.includes(activity.id))
    .sort((left, right) => left.code.localeCompare(right.code, "de"));

  return choosable;
}

/**
 * Keeps an activity only if it can be chosen for the project; otherwise the project time goes
 * without an activity, which never blocks anything (`PINV-P07`).
 *
 * @param myProjects - Projects, activities and allowed lists of the person.
 * @param projectId - The project.
 * @param activityId - The wanted activity.
 * @returns The activity, or `null`.
 */
export function choosableActivity(
  myProjects: MyProjects | null,
  projectId: string | null,
  activityId: string | null,
): string | null {
  const choosable =
    activityId !== null &&
    activitiesForProject(myProjects, projectId).some((activity) => activity.id === activityId);
  const kept = choosable ? activityId : null;

  return kept;
}

/**
 * Formats an activity for selections and lists.
 *
 * @param activity - The activity.
 * @returns Code and name.
 */
export function activityLabel(activity: ActivityOption): string {
  const label = `${activity.code} · ${activity.name}`;
  return label;
}
